// Grids & Gantts — a reader's own view of a grid.
//
// Standard 1.7: THE COLUMNS ARE THE ADMIN'S; THE VIEW IS THE READER'S.
//
// A structure's column set is a shared decision — it is what the page is FOR,
// and it is edited once, by an admin, for everybody. But two people reading
// the same structure are answering different questions, and one of them
// always wants a column the other does not care about. Making that an admin
// request is how a page acquires nineteen columns nobody can switch off.
//
// So there are two layers. The config says what the page IS. On top of it,
// each reader keeps their own:
//
//   added    columns they put there — any field this site already has, so
//            nothing is invented and nothing needs a migration. Drawn
//            UNDERLINED, because a reader looking at a colleague's screen
//            should be able to see instantly which columns are not the
//            page's own.
//   hidden   columns they have put away. The config's, not gone — theirs is
//            a view, and it can always be reset.
//   widths   what each column is worth to THEM. An admin's width is the
//            starting point, not a ruling.
//   order    where each column sits, for THEM — dragged by its heading. The
//            page's own columns and their own, in any order they like.
//
// WHERE IT IS KEPT, and the honest limit. This repo has no per-user
// preference store a client page may write to: the one that exists
// (`/api/user-columns`) belongs to another module's table and is keyed by an
// integer structure id with no scope of its own, and a new table would mean
// editing `modules/`, which a client fork does not do. So a view lives in
// THIS BROWSER — the same place every other Stride page keeps a
// reader's choices — and that is a real limit: it does not follow anybody to
// a second machine.
//
// The seam is `readStore` / `writeStore`. When the product grows a per-user
// store, those two functions change and nothing else does.
//
// Everything above them is pure, so the merge — which is the part that can be
// wrong — is tested under node.

// The widest a column may be set — by a drag, or by a width written down
// when a drag starts. 2400px: wide enough that a column the grid stretched
// across a very large screen is written down at the width it is SHOWN at
// (at 900 the first drag on such a screen visibly shrank it), and still a
// bound on a bad value read back from storage.
export const MAX_COL_W = 2400;

const KEY = (storeKey) => `grids.${storeKey || 'grid'}.view`;

// SOME COLUMNS NAME NO FIELD, and they are not broken.
//
// The two counts, the progress bar, the key, the tree, the status and the
// work type all come from the ROW rather than from a field on it. Requiring
// a field name silently threw every one of them away — a reader could add
// "Progress" from the menu and nothing happened at all, which is the worst
// kind of bug because it looks like a dead button.
const NO_FIELD = ['key', 'summary', 'status', 'type', 'count', 'countSub', 'progress', 'dueIn'];
export const needsField = (kind) => !NO_FIELD.includes(String(kind || ''));

export const EMPTY_VIEW = Object.freeze({ added: [], hidden: [], widths: {}, sort: null, order: [] });

// What a stored view is allowed to be. A view is read back from storage a
// build or two after it was written, so every field is checked rather than
// trusted — a column whose field no longer exists is dropped when it is
// merged, not here, because only the merge knows the field list.
export function normalizeView(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const widths = {};
  if (r.widths && typeof r.widths === 'object') {
    Object.keys(r.widths).forEach((id) => {
      const n = Number(r.widths[id]);
      if (Number.isFinite(n) && n >= 40 && n <= MAX_COL_W) widths[String(id)] = Math.round(n);
    });
  }
  return {
    added: (Array.isArray(r.added) ? r.added : [])
      .map((c, i) => (c && (c.field || !needsField(c.kind)) ? {
        id: String(c.id || `mine-${i}`),
        field: String(c.field || ''),
        label: String(c.label || c.field),
        kind: String(c.kind || 'text'),
        align: c.align === 'right' ? 'right' : (c.align === 'center' ? 'center' : 'left'),
        width: Number.isFinite(Number(c.width)) ? Math.round(Number(c.width)) : 140,
        agg: c.agg === 'sum' ? 'sum' : 'none',
        mine: true,
      } : null))
      .filter(Boolean),
    hidden: [...new Set((Array.isArray(r.hidden) ? r.hidden : []).map(String))],
    widths,
    // A SORT IS A READER'S TOO. The structure's own order is the admin's
    // answer to "how does this page read"; a reader asking "which of these is
    // latest" is not overruling that, they are looking at it a second way,
    // and they are the only one who sees it.
    sort: r.sort && r.sort.id
      ? { id: String(r.sort.id), dir: r.sort.dir === 'desc' ? 'desc' : 'asc' }
      : null,
    // AN ORDER NAMES COLUMN IDS, nothing else. An id that no longer exists
    // is harmless — the merge only ever places columns it was given.
    order: [...new Set((Array.isArray(r.order) ? r.order : []).filter((x) => x != null && x !== '').map(String))],
  };
}

// ---- the seam ----------------------------------------------------------------
export function readStore(storeKey) {
  try { return normalizeView(JSON.parse(localStorage.getItem(KEY(storeKey)) || '{}')); }
  catch { return normalizeView(null); }
}

export function writeStore(storeKey, view) {
  try {
    const v = normalizeView(view);
    if (isEmptyView(v)) localStorage.removeItem(KEY(storeKey));
    else localStorage.setItem(KEY(storeKey), JSON.stringify(v));
  } catch { /* storage may be blocked; a view is a convenience, not a record */ }
  return view;
}

export const isEmptyView = (view) => {
  const v = normalizeView(view);
  return !v.added.length && !v.hidden.length && !Object.keys(v.widths).length && !v.sort && !v.order.length;
};

// ---- the merge ---------------------------------------------------------------
// The columns actually drawn: the page's own, minus what this reader has put
// away, plus what they have added, each at the width they gave it.
//
// A COLUMN WHOSE FIELD HAS GONE IS DROPPED. A reader's view outlives a field
// being renamed or deleted on the site, and a column of blanks with no
// explanation is worse than a column that quietly stops being there. When a
// `fieldList` is supplied, an added column has to name something in it.
//
// `alsoKnown(name)` is for the fields a page answers ITSELF — the structural
// pseudo-fields (the project, its name, its category) that are not in any
// site's field list because they are not fields on the work item. Without
// it, "Project" was added, stored, and silently never drawn.
export function mergeUserView(cols, view, { fieldList = null, alsoKnown = null } = {}) {
  const v = normalizeView(view);
  const hidden = new Set(v.hidden);
  const listed = fieldList
    ? new Set(fieldList.map((f) => String(f && (f.name || f.id || f.key) || '').toLowerCase()).filter(Boolean))
    : null;
  const known = listed
    ? { has: (name) => listed.has(name) || (typeof alsoKnown === 'function' && !!alsoKnown(name)) }
    : null;
  const width = (c) => (v.widths[c.id] != null ? v.widths[c.id] : c.width);

  const own = (cols || [])
    // A PIN OVERRULES A HIDE, including one stored before the column was
    // pinned — otherwise pinning a column would leave it invisible to
    // exactly the readers who had already put it away.
    .filter((c) => isPinned(c) || !hidden.has(c.id))
    .map((c) => ({ ...c, width: width(c), mine: false }));

  const mine = v.added
    .filter((c) => !hidden.has(c.id))
    // A column naming a field that has gone is dropped. One that names no
    // field — a count, the progress bar — has nothing to check and is kept.
    .filter((c) => !needsField(c.kind) || !known || known.has(String(c.field).toLowerCase()))
    // A reader cannot add a column the page already has: it would draw the
    // same value twice and the second one would look like a mistake. For a
    // column with a field that means the same FIELD; for one without, the
    // same KIND, because two Progress columns are two of the same column.
    .filter((c) => !(cols || []).some((x) => (needsField(c.kind)
      ? String(x.field || '').toLowerCase() === String(c.field).toLowerCase()
      : x.kind === c.kind)))
    .map((c) => ({ ...c, width: width(c), mine: true, on: true }));

  return applyOrder([...own, ...mine], v.order);
}

// THE READER'S ORDER, laid over the page's.
//
// The columns the reader has placed come in the order they placed them. A
// column they have never placed — one an admin added after they last
// dragged, one they have just added themselves — is not thrown to the end:
// it keeps its place after the column it followed on the page, so a new
// column appears where the admin put it rather than somewhere surprising.
export function applyOrder(list, order) {
  const ids = Array.isArray(order) ? order.map(String) : [];
  if (!ids.length) return list;
  const rank = new Map(ids.map((id, i) => [id, i]));
  const placed = list.filter((c) => rank.has(String(c.id)))
    .sort((a, b) => rank.get(String(a.id)) - rank.get(String(b.id)));
  const out = placed.slice();
  list.forEach((c, i) => {
    if (rank.has(String(c.id))) return;
    // After the nearest column before it (on the page) that is already out.
    let at = 0;
    for (let j = i - 1; j >= 0; j -= 1) {
      const k = out.findIndex((x) => x.id === list[j].id);
      if (k >= 0) { at = k + 1; break; }
    }
    out.splice(at, 0, c);
  });
  return out;
}

// DRAGGING A HEADING. `ids` is the order on screen now; the column `id` is
// dropped before `beforeId` (or at the end, when that is null). The whole
// on-screen order is written down, so what the reader sees is what is kept.
export const withMoved = (view, ids, id, beforeId) => {
  const v = normalizeView(view);
  const key = String(id);
  const rest = (ids || []).map(String).filter((x) => x !== key);
  if (rest.length === (ids || []).length) return v;
  const at = beforeId == null ? rest.length : rest.indexOf(String(beforeId));
  rest.splice(at < 0 ? rest.length : at, 0, key);
  return { ...v, order: rest };
};

// ---- the small changes a reader makes ----------------------------------------
// EVERY COLUMN'S WIDTH AT ONCE — the snapshot taken the moment a reader
// first reaches for a resize grip.
//
// Without it the first drag was a jump: until then the columns are FITTED to
// the pane, and locking one turns the fitting off for all of them, so every
// other column sprang from its fitted width to its configured one. Writing
// down what they all currently ARE means the fitting stops changing nothing,
// and only the column under the mouse moves.
export const withWidths = (view, map) => {
  const v = normalizeView(view);
  const widths = { ...v.widths };
  Object.keys(map || {}).forEach((id) => {
    const n = Number(map[id]);
    if (Number.isFinite(n)) widths[String(id)] = Math.max(40, Math.min(MAX_COL_W, Math.round(n)));
  });
  return { ...v, widths };
};

export const withWidth = (view, id, px) => {
  const v = normalizeView(view);
  return { ...v, widths: { ...v.widths, [String(id)]: Math.max(40, Math.min(MAX_COL_W, Math.round(px))) } };
};

export const withAdded = (view, col) => {
  const v = normalizeView(view);
  const id = String(col.id || `mine-${Date.now().toString(36)}`);
  return {
    ...v,
    // Adding back something that was put away is UNHIDING it, not a second
    // copy of it.
    hidden: v.hidden.filter((h) => h !== id),
    added: [...v.added.filter((c) => c.id !== id), { ...col, id, mine: true }],
  };
};

// PINNED — an admin's answer to "this column is the point of the page".
//
// A structure exists to show something, and a reader who hides the column it
// exists to show is looking at a different page by accident. So an admin may
// pin a column, and a pin is the one thing a view cannot argue with: it
// cannot be hidden and it cannot be removed. Its WIDTH is still the reader's,
// because a pin is about what is shown, not how wide it is.
export const isPinned = (col) => !!(col && col.pinned);

// Removing one of the reader's own columns forgets it; removing one of the
// page's own only puts it away, because it is not theirs to delete. A pinned
// one goes nowhere at all.
export const withRemoved = (view, id, { pinnedIds = [] } = {}) => {
  const v = normalizeView(view);
  const key = String(id);
  if (pinnedIds.map(String).includes(key)) return v;
  const wasMine = v.added.some((c) => c.id === key);
  const widths = { ...v.widths };
  delete widths[key];
  return wasMine
    ? { ...v, added: v.added.filter((c) => c.id !== key), widths }
    : { ...v, hidden: [...new Set([...v.hidden, key])] };
};

export const withShown = (view, id) => {
  const v = normalizeView(view);
  return { ...v, hidden: v.hidden.filter((h) => h !== String(id)) };
};

// Clicking a heading walks a column through ascending, descending, and back
// to the structure's own order — the third state matters, because a reader
// who sorted to answer one question needs a way back to the tree the page was
// built to show.
export const withSort = (view, id) => {
  const v = normalizeView(view);
  const key = String(id);
  if (!v.sort || v.sort.id !== key) return { ...v, sort: { id: key, dir: 'asc' } };
  if (v.sort.dir === 'asc') return { ...v, sort: { id: key, dir: 'desc' } };
  return { ...v, sort: null };
};

export const sortOf = (view, id) => {
  const v = normalizeView(view);
  return v.sort && v.sort.id === String(id) ? v.sort.dir : null;
};

export const resetView = () => ({ added: [], hidden: [], widths: {}, sort: null, order: [] });
